import React from 'react';
import CustomPanel from 'core/dateRangePickerRelative/panels/CustomPanel';
import { CustomTextInputPanel } from 'core/dateRangePickerRelative/panels/CustomTextInputPanel';
import QueryBuilderPresetsPanel from 'core/dateRangePickerRelative/panels/QueryBuilderPresetsPanel';
import { SingleDatePickerWithCollapsibleCalendar } from 'ui/datePicker';
import noop from 'utils/noop';
import { formatDateForSave, getNewDate } from 'utils/date';
import {
  formatToES,
  parseFromESToDateRange,
  UNIT_VALUES,
  UNIT_MODIFIERS,
  UNIT_MODIFIER_VALUES,
  presets,
} from 'utils/relativeDateTime';
import { getDateFromValue, getIsDateExact } from './helpers';
import {
  StyledCoreDateRangePickerRelative,
  StyledCoreStackedToggle,
} from './CriteriaPropertyField.styled';

// type Props = {
//   /** Class of the text field. */
//   className: string,
//   /** If the text field is disabled. */
//   disabled: boolean,
//   /** Array comtaining the group index, query index and criteria property index. */
//   fieldIdentifier: [],
//   /** If the field is dynamic. */
//   isDynamic: boolean,
//   /** Callback function when field value is changed. */
//   onChange: () => {},
//   /** The value of the field. */
//   value: string,
//   /** Show custom date text input */
//   showCustomDateTextInput: boolean,
// };

export default class CriteriaDateField extends React.Component {
  static defaultProps = {
    fieldIdentifier: [],
    onChange: noop,
    showCustomDateTextInput: false,
    value: '',
    disabled: false,
  };

  componentDidMount = () => {
    /** 
    For when a CriterDateField needs to be rendered with a DateRangePickerRelative component and has no set value, we need to exercise the change handler.  
    This allows for the validation to be ran against the CriteriaDateField and not fail because the value is not present in the store.
    */
    const { value } = this.props;
    const isExactDate = getIsDateExact(value);
    if (!isExactDate && !value) {
      this.handleChangeRelativeDate({
        startRange: presets.LAST_5_DAYS.startRange,
        endRange: 'n',
        isTextInput: undefined,
      });
    }
  };

  /**
   * handleToggleDate
   * Callback function when absolute / relative toggle is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props with new toggle value.
   * @returns { void }
   */
  handleToggleDate = (e, { value }) => {
    const { fieldIdentifier, onChange } = this.props;
    if (value) {
      onChange(`${UNIT_MODIFIERS.n}-24${UNIT_VALUES.hour}`, ...fieldIdentifier);
    } else {
      onChange(`${formatDateForSave(getNewDate())}`, ...fieldIdentifier);
    }
  };

  /**
   * handleDateChange
   * Callback function when the date value changes.
   * @param { Moment } selectedDate: The newly selected date value.
   * @returns { void }
   */
  handleDateChange = (selectedDate) => {
    const { fieldIdentifier, onChange } = this.props;
    const date = formatDateForSave(selectedDate);
    onChange(`${date.valueOf()}`, ...fieldIdentifier);
  };

  /**
   * handleChangeRelativeDate
   * Callback function when relative date is changed.
   * @param { Object } updatedRanges: The updated start and end relative time ranges.
   * @returns { void }
   */
  handleChangeRelativeDate = ({ startRange, endRange, isTextInput }) => {
    const { fieldIdentifier, onChange } = this.props;
    // Parser function send now+to backend, other is now
    if (isTextInput) {
      onChange(startRange, ...fieldIdentifier);
    } else {
      if (startRange !== UNIT_MODIFIER_VALUES.now) {
        onChange(`${formatToES(startRange)}`, ...fieldIdentifier);
      }
      if (endRange !== UNIT_MODIFIER_VALUES.now) {
        onChange(`${formatToES(endRange)}`, ...fieldIdentifier);
      }
    }
  };

  render() {
    const { className, disabled, value, showCustomDateTextInput } = this.props;
    const isExactDate = getIsDateExact(value);
    let input;
    if (isExactDate) {
      const dateTime = getDateFromValue(value);
      input = (
        <SingleDatePickerWithCollapsibleCalendar
          className={className}
          date={dateTime}
          horizontalAlignment="right"
          isDisabled={disabled}
          isTimeZonePickerDisabled
          onDateChange={this.handleDateChange}
          showTime
          useRelativeDate={false}
        />
      );
    } else {
      const { startRange, endRange } = value && parseFromESToDateRange(value);
      input = (
        <StyledCoreDateRangePickerRelative
          disabled={disabled}
          combineToAndFrom
          className={className}
          endRange={endRange}
          hideParsedDateLabelFromInput
          onChangeRange={this.handleChangeRelativeDate}
          panels={[
            QueryBuilderPresetsPanel,
            showCustomDateTextInput ? CustomTextInputPanel : CustomPanel,
          ]}
          showInPopup
          startRange={startRange}
          value={value}
        />
      );
    }
    return (
      <>
        <StyledCoreStackedToggle
          falseLabel="Exact"
          onChange={this.handleToggleDate}
          trueLabel="Relative"
          value={!isExactDate}
          disabled={disabled}
        />
        {input}
      </>
    );
  }
}
